import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import { SaveButton, SelectArrayInput } from "react-admin";
import EditIcon from "@mui/icons-material/EditOutlined";
import { Row } from "../../misc/Row";
import { Student } from "../../providers/studentsProvider";
import { DrivingLicenseClass } from "../../model/DrivingLicenseClass";
import { Form2 } from "../../misc/Form2";

export function BookedTrainingsField({
  student,
  bookedTrainingIds,
  handleSubmit,
}: {
  student: Student;
  bookedTrainingIds?: string[];
  handleSubmit: (bookedTrainingIds: string[]) => Promise<void>;
}) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const bookedTrainings = (bookedTrainingIds ?? []).reduce<{ id: string; drivingLicenseClass: DrivingLicenseClass }[]>(
    (acc, bookedTrainingId) => {
      const training = student.bookedTrainings.find((bt) => bt.id === bookedTrainingId);
      if (training) {
        return [...acc, { id: training.id, drivingLicenseClass: training.drivingLicenseClass }];
      }
      return acc;
    },
    [],
  );
  const drivingLicenseClasses = bookedTrainings.map((bt) => bt.drivingLicenseClass);

  let currentClasses = "???";
  if (drivingLicenseClasses.length === 1) {
    currentClasses = drivingLicenseClasses[0];
  } else if (drivingLicenseClasses.length > 1) {
    currentClasses = drivingLicenseClasses.join(", ");
  }

  const onSubmit = async (values: FieldValues) => {
    setIsSubmitting(true);
    await runSafely(() => handleSubmit(values.bookedTrainingIds), {
      errorMessage: "Fehler beim Aktualisieren der Führerscheinklasse.",
      successMessage: "Die Führerscheinklasse wurde erfolgreich aktualisiert.",
      onSuccess: () => setIsEditing(false),
    });
    setIsSubmitting(false);
  };

  const canEdit = drivingLicenseClasses.length === 0 || Object.keys(student.bookedTrainings).length > 1;

  return (
    <>
      {isEditing ? (
        <Form2 values={{ bookedTrainingIds: bookedTrainingIds }} onSubmit={onSubmit}>
          <Row gap={0.5} alignItems="center">
            {isSubmitting ? (
              <CircularProgress size={32} sx={{ ml: 2 }} />
            ) : (
              <Row gap={1} alignItems="center">
                <SelectArrayInput
                  source="bookedTrainingIds"
                  label={false}
                  helperText={false}
                  sx={{
                    "& .MuiInputBase-root": { height: 36 },
                    "& .MuiChip-root": { mt: 0 },
                  }}
                  choices={student.bookedTrainings.map((bt) => ({ id: bt.id, name: bt.drivingLicenseClass }))}
                />
                <SaveButton icon={<></>} size="small" sx={{ mt: "2px" }} />
              </Row>
            )}
          </Row>
        </Form2>
      ) : (
        <>
          <Typography variant="body2">{currentClasses}</Typography>
          {canEdit && (
            <IconButton onClick={() => setIsEditing(true)} size="small" data-testid="editBookedTraingingsButton">
              <EditIcon />
            </IconButton>
          )}
        </>
      )}
    </>
  );
}
